import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import { fetchEquipmentsForManagement, deleteEquipment } from '../../services/api';
import { hasPermission } from '../../services/authService';
import './EquipmentListPage.css';

function EquipmentListPage() {
    const navigate = useNavigate();
    const [selectedStatus, setSelectedStatus] = useState('전체');
    const [selectedFactory, setSelectedFactory] = useState('전체');
    const [selectedLine, setSelectedLine] = useState('전체');
    const [searchQuery, setSearchQuery] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [equipments, setEquipments] = useState([]);
    const [allEquipments, setAllEquipments] = useState([]);
    const [factories, setFactories] = useState([]);
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(true);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    // 권한 체크
    const canCreate = hasPermission('equipment.create');
    const canUpdate = hasPermission('equipment.update');
    const canDelete = hasPermission('equipment.delete');

    // API에서 설비 목록 가져오기
    useEffect(() => {
        const loadEquipments = async () => {
            try {
                setLoading(true);
                const data = await fetchEquipmentsForManagement();
                

                // API 데이터를 화면에 맞게 변환
                const equipmentsData = Array.isArray(data) ? data : [];
                const transformedEquipments = equipmentsData.map(equipment => ({
                    id: equipment.id,
                    name: equipment.equipmentName,
                    code: equipment.equipmentCode,
                    model: equipment.model || '-',
                    line: equipment.lineName || '-',
                    manufacturer: equipment.manufacturer || '-',
                    serialNumber: equipment.serialNumber || '-',
                    status: mapStatus(equipment.status),
                    maxTemperature: equipment.maxTemperature ? `${equipment.maxTemperature}°C` : '-',
                    maxPressure: equipment.maxPressure ? `${equipment.maxPressure}MPa` : '-',
                    cycleTime: equipment.targetCycleTime ? `${(equipment.targetCycleTime / 1000).toFixed(1)}s` : '-',
                    cavityCount: equipment.cavityCount || '-',
                    lastInspection: equipment.lastInspectionDate || '-',
                    factory: equipment.factoryName || '-',
                    factoryId: equipment.factoryId,
                    lineId: equipment.lineId
                }));

                setAllEquipments(transformedEquipments);
                setEquipments(transformedEquipments);

                // 공장 목록 추출 (중복 제거)
                const uniqueFactories = [...new Set(transformedEquipments.map(e => e.factory))].filter(f => f !== '-');
                setFactories(uniqueFactories.sort());

                // 라인 목록 추출 (중복 제거)
                const uniqueLines = [...new Set(transformedEquipments.map(e => e.line))].filter(l => l !== '-');
                setLines(uniqueLines.sort());
            } catch (err) {
                console.error('설비 목록 조회 실패:', err);
                setAllEquipments([]);
                setEquipments([]);
            } finally {
                setLoading(false);
            }
        };

        loadEquipments();
    }, []);

    // 상태 매핑 함수
    const mapStatus = (apiStatus) => {
        const statusMap = {
            'RUNNING': '가동',
            'IDLE': '대기',
            'STOPPED': '정지',
            'MAINTENANCE': '점검',
            'FAULT': '고장',
            'OFFLINE': '오프라인'
        };
        return statusMap[apiStatus] || '대기';
    };

    // 필터링 로직
    useEffect(() => {
        let filtered = [...allEquipments];

        // 공장 필터링
        if (selectedFactory !== '전체') {
            filtered = filtered.filter(equipment => equipment.factory === selectedFactory);
        }

        // 라인 필터링
        if (selectedLine !== '전체') {
            filtered = filtered.filter(equipment => equipment.line === selectedLine);
        }

        // 상태 필터링
        if (selectedStatus !== '전체') {
            filtered = filtered.filter(equipment => equipment.status === selectedStatus);
        }

        // 검색어 필터링
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(equipment =>
                equipment.name.toLowerCase().includes(query) ||
                equipment.model.toLowerCase().includes(query) ||
                equipment.code.toLowerCase().includes(query)
            );
        }

        setEquipments(filtered);
        setPage(0); // 필터 변경 시 첫 페이지로
    }, [selectedFactory, selectedLine, selectedStatus, searchQuery, allEquipments]);

    // 페이지네이션 적용
    const paginatedEquipments = useMemo(() => {
        const startIndex = page * size;
        return equipments.slice(startIndex, startIndex + size);
    }, [equipments, page, size]);

    const totalElements = equipments.length;
    const totalPages = Math.ceil(totalElements / size);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const getStatusClass = (status) => {
        switch (status) {
            case '가동':
                return 'equipment-status-running';
            case '대기':
                return 'equipment-status-standby';
            case '점검':
                return 'equipment-status-maintenance';
            case '고장':
                return 'equipment-status-error';
            default:
                return 'equipment-status-standby';
        }
    };

    const handleEquipmentClick = (equipmentId) => {
        navigate(`/equipment/${equipmentId}`);
    };

    const handleEditClick = (equipmentId) => {
        navigate(`/equipment/${equipmentId}?edit=true`);
    };

    const handleAddEquipment = () => {
        navigate('/equipment/register');
    };

    const handleDeleteClick = (equipment) => {
        setSelectedEquipment(equipment);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            
            await deleteEquipment(selectedEquipment.id);

            alert(`"${selectedEquipment.name}" 설비가 삭제되었습니다.`);
            setIsDeleteModalOpen(false);

            // 목록 새로고침
            const data = await fetchEquipmentsForManagement();
            const equipmentsData = Array.isArray(data) ? data : [];
            const transformedEquipments = equipmentsData.map(equipment => ({
                id: equipment.id,
                name: equipment.equipmentName,
                code: equipment.equipmentCode,
                model: equipment.model || '-',
                line: equipment.lineName || '-',
                manufacturer: equipment.manufacturer || '-',
                serialNumber: equipment.serialNumber || '-',
                status: mapStatus(equipment.status),
                maxTemperature: equipment.maxTemperature ? `${equipment.maxTemperature}°C` : '-',
                maxPressure: equipment.maxPressure ? `${equipment.maxPressure}MPa` : '-',
                cycleTime: equipment.targetCycleTime ? `${(equipment.targetCycleTime / 1000).toFixed(1)}s` : '-',
                cavityCount: equipment.cavityCount || '-',
                lastInspection: equipment.lastInspectionDate || '-',
                factory: equipment.factoryName || '-',
                factoryId: equipment.factoryId,
                lineId: equipment.lineId
            }));

            setAllEquipments(transformedEquipments);
            setEquipments(transformedEquipments);
        } catch (error) {
            console.error('설비 삭제 실패:', error);
            alert('설비 삭제에 실패했습니다.');
        }
    };

    return (
        <MainLayout>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
                    데이터를 불러오는 중...
                </div>
            ) : (
            <div className="equipment-list-container">
                {/* Header Section */}
                <div className="equipment-list-header">
                    <div className="equipment-list-header-left">
                        <div className="equipment-list-title">설비 관리</div>
                        <p className="equipment-list-subtitle">설비 현황 및 운영 상태 관리</p>
                    </div>
                    <div className="equipment-list-header-right">
                        {/* 추가 정보 표시 가능 */}
                    </div>
                </div>

                <div className="equipment-list-content">
                    {/* Filter and Search Section */}
                    <div className="equipment-list-controls">
                        <div className="equipment-control-left">
                            <label className="equipment-control-label">공장</label>
                            <div className="equipment-dropdown-container">
                                <select
                                    className="equipment-status-dropdown"
                                    value={selectedFactory}
                                    onChange={(e) => setSelectedFactory(e.target.value)}
                                >
                                    <option value="전체">전체</option>
                                    {factories.map(factory => (
                                        <option key={factory} value={factory}>{factory}</option>
                                    ))}
                                </select>
                                <svg className="equipment-dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>

                            <label className="equipment-control-label" style={{ marginLeft: '16px' }}>라인</label>
                            <div className="equipment-dropdown-container">
                                <select
                                    className="equipment-status-dropdown"
                                    value={selectedLine}
                                    onChange={(e) => setSelectedLine(e.target.value)}
                                >
                                    <option value="전체">전체</option>
                                    {lines.map(line => (
                                        <option key={line} value={line}>{line}</option>
                                    ))}
                                </select>
                                <svg className="equipment-dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>

                            <label className="equipment-control-label" style={{ marginLeft: '16px' }}>상태</label>
                            <div className="equipment-dropdown-container">
                                <select
                                    className="equipment-status-dropdown"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    <option value="전체">전체</option>
                                    <option value="가동">가동</option>
                                    <option value="대기">대기</option>
                                    <option value="점검">점검</option>
                                    <option value="고장">고장</option>
                                </select>
                                <svg className="equipment-dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        <div className="equipment-control-right">
                            <div className="equipment-search-container">
                                <input
                                    type="text"
                                    className="equipment-search-input"
                                    placeholder="설비명 또는 모델 검색"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <svg className="equipment-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            {canCreate && (
                                <button className="equipment-add-btn" onClick={handleAddEquipment}>
                                    <svg className="equipment-plus-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    설비 추가
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Equipment Table */}
                    <div className="equipment-table-container">
                        <table className="equipment-table">
                            <thead>
                                <tr>
                                    <th>설비명</th>
                                    <th>모델</th>
                                    <th>공장</th>
                                    <th>라인</th>
                                    <th>제조사</th>
                                    <th>시리얼넘버</th>
                                    <th>상태</th>
                                    <th>최대온도</th>
                                    <th>최대압력</th>
                                    <th>사이클타임</th>
                                    <th>캐비티</th>
                                    <th>점검일</th>
                                    {(canUpdate || canDelete) && <th>작업</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedEquipments.map((equipment) => (
                                    <tr key={equipment.id} onClick={() => handleEquipmentClick(equipment.id)} style={{ cursor: 'pointer' }}>
                                        <td className="equipment-name-cell">
                                            <div className="equipment-name-info">
                                                <div className="equipment-icon">
                                                    <span className="material-symbols-outlined">precision_manufacturing</span>
                                                </div>
                                                <div className="equipment-details">
                                                    <div className="equipment-name">{equipment.name}</div>
                                                    <div className="equipment-factory">{equipment.factory}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="equipment-model">{equipment.model}</span>
                                        </td>
                                        <td>{equipment.factory}</td>
                                        <td>{equipment.line}</td>
                                        <td>{equipment.manufacturer}</td>
                                        <td>{equipment.serialNumber}</td>
                                        <td>
                                            <span className={`equipment-status-badge ${getStatusClass(equipment.status)}`}>
                                                {equipment.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="equipment-metric-value">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '4px' }}>
                                                    <path d="M7 2V8M7 8C6.46957 8 5.96086 8.21071 5.58579 8.58579C5.21071 8.96086 5 9.46957 5 10C5 10.5304 5.21071 11.0391 5.58579 11.4142C5.96086 11.7893 6.46957 12 7 12C7.53043 12 8.03914 11.7893 8.41421 11.4142C8.78929 11.0391 9 10.5304 9 10C9 9.46957 8.78929 8.96086 8.41421 8.58579C8.03914 8.21071 7.53043 8 7 8Z" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {equipment.maxTemperature}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="equipment-metric-value">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '4px' }}>
                                                    <path d="M7 2V12M2 7H12" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {equipment.maxPressure}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="equipment-cycle-time">{equipment.cycleTime}</span>
                                        </td>
                                        <td>
                                            <span className="equipment-cavity-count">{equipment.cavityCount}</span>
                                        </td>
                                        <td>{equipment.lastInspection}</td>
                                        {(canUpdate || canDelete) && (
                                            <td>
                                                <div className="equipment-action-buttons" onClick={(e) => e.stopPropagation()}>
                                                    {canUpdate && (
                                                        <button className="equipment-action-btn equipment-edit-btn" onClick={() => handleEditClick(equipment.id)}>
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <path d="M11.3333 2C11.5084 1.82493 11.7163 1.68605 11.9457 1.59135C12.1752 1.49665 12.4218 1.44803 12.6707 1.44803C12.9196 1.44803 13.1662 1.49665 13.3956 1.59135C13.6251 1.68605 13.833 1.82493 14.008 2C14.183 2.17507 14.3219 2.38299 14.4166 2.61245C14.5113 2.84191 14.5599 3.08852 14.5599 3.33744C14.5599 3.58636 14.5113 3.83297 14.4166 4.06243C14.3219 4.29189 14.183 4.49981 14.008 4.67488L5.33333 13.3495L1.33333 14.683L2.66667 10.6829L11.3333 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button className="equipment-action-btn equipment-delete-btn" onClick={() => handleDeleteClick(equipment)}>
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <path d="M2 4H3.33333H14M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33333C9.68696 1.33333 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4H12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            size={size}
                            onPageChange={handlePageChange}
                            onSizeChange={handleSizeChange}
                        />
                    </div>
                </div>
            </div>
            )}

            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="설비 삭제"
                message={selectedEquipment ? `"${selectedEquipment.name}" 설비를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.` : ''}
                confirmText="삭제"
                cancelText="취소"
                type="danger"
            />
        </MainLayout>
    );
}

export default EquipmentListPage;
